/**
 * What the agent is doing right now, from what the client already receives: live turns (turn.started / turn.done),
 * mission runs (run.upsert) and the proactivity pause (settings). Drives the avatar's ring and its status label.
 */
import type { AgentSettings, MissionRunView } from "../../shared/contracts";
import { useAgentWorking, useRuns, useSettings } from "./agent";
import { useNow } from "./hooks";
import { isSnoozed } from "./snooze";

/** thinking: a turn is running · computer: a run works on a computer · working: a run without one · paused · idle. */
export type PresenceState = "thinking" | "computer" | "working" | "paused" | "idle";

export interface Presence {
  state: PresenceState;
  /** « réfléchit… », « travaille sur son ordinateur »… (after the agent's name). */
  label: string;
  /** The run it is working on, if any. */
  run: MissionRunView | null;
}

export const PRESENCE_LABEL: Record<PresenceState, string> = {
  thinking: "réfléchit…",
  computer: "travaille sur son ordinateur",
  working: "travaille sur une mission",
  paused: "en pause",
  idle: "veille sur tes affaires",
};

/** Pure rule, shared by the hook and the pages that already hold the data (Pulse). */
export function presenceOf(input: { working: boolean; runs: MissionRunView[] | undefined; settings: AgentSettings | undefined; now: number }): Presence {
  const run = input.runs?.find((r) => r.status === "running") ?? null;
  let state: PresenceState = "idle";
  if (input.working) state = "thinking";
  else if (run) state = run.machine === "none" ? "working" : "computer";
  else if (input.settings && isSnoozed(input.settings, input.now)) state = "paused";
  return { state, label: PRESENCE_LABEL[state], run };
}

export function usePresence(): Presence {
  const settings = useSettings();
  const working = useAgentWorking();
  const runs = useRuns({ poll: false });
  const now = useNow(30_000);
  return presenceOf({ working, runs: runs.data, settings: settings.data, now });
}
